import {
  ApprovalQueueView,
  GovernanceCommandView,
  QueryMetadata,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { z } from 'zod';
import { filterParams } from './filterParams';
import { apiFetch } from './apiFetch';

/** One entry in an entity's change log, as `GET /governance/audit/:id` returns it. */
export interface AuditEvent {
  action: string;
  occurredAt: string;
  actor: string | null;
  reason: string | null;
  before: unknown;
  after: unknown;
}

/**
 * The approval queue and its decisions.
 *
 * Not part of the `Repositories` port yet: that interface predates the write
 * paths, and every method on it returns a read model. Adding submit/approve
 * there means deciding how commands are modelled across the whole surface,
 * which is a larger change than this screen should force. Kept alongside it
 * for now, behind the same `apiFetch` so identity travels the same way.
 */
const CommandEnvelope = z.object({ data: GovernanceCommandView, meta: QueryMetadata });

export function createHttpGovernanceRepository(baseUrl: string) {
  return {
    /**
     * The command centre, from the governance tables.
     *
     * The one method here that *is* on the `Repositories` port, because it is a
     * read model like every other. It counted a fixture's queues while the real
     * ones filled up beside it.
     */
    async getCommandCenter(filters: GlobalFilters): Promise<QueryResult<GovernanceCommandView>> {
      const response = await apiFetch(
        `${baseUrl}/admin/command-center?${filterParams(filters).toString()}`,
      );
      if (!response.ok) throw new Error(`GET /admin/command-center failed: ${response.status}`);
      return CommandEnvelope.parse((await response.json()) as unknown);
    },

    async listApprovals(): Promise<ApprovalQueueView> {
      const response = await apiFetch(`${baseUrl}/governance/approvals`);
      if (!response.ok) {
        throw new Error(`GET /governance/approvals failed: ${response.status}`);
      }
      return ApprovalQueueView.parse(await response.json());
    },

    async auditFor(entityId: string): Promise<AuditEvent[]> {
      const response = await apiFetch(`${baseUrl}/governance/audit/${entityId}`);
      // 404 is the same answer as "not in your scope" by design (see the route),
      // and an empty trail is the honest rendering of both.
      if (response.status === 404) return [];
      if (!response.ok) throw new Error('The change history could not be loaded.');
      const body = (await response.json()) as { data: { rows: AuditEvent[] } };
      return body.data.rows;
    },

    async decide(
      decisionId: string,
      approve: boolean,
      comments: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/governance/approvals/${decisionId}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ approve, comments }),
      });
      if (response.ok) return { ok: true };
      // The server's message is shown as-is: it explains *why* — self-approval,
      // already decided, no comment — and a generic "could not approve" would
      // discard the only useful part of the response.
      const body = (await response.json()) as { message?: string };
      return { ok: false, message: body.message ?? `Request failed (${response.status}).` };
    },
  };
}
